// Splash gate shown before the rest of the app loads. The user has
// two ways past it:
//
//   1. "Activate" tab — type a one-time license activation code.
//      We POST it to /licenses/check to confirm it exists and
//      hasn't been redeemed, store it in localStorage, and
//      unlock. Signup later pulls the same code back out and
//      consumes it on the server.
//
//   2. "Sign in" tab — type the email + password of an existing
//      account. The signin response includes the user's bound
//      license code, which AuthProvider writes back into
//      localStorage. Lets a teacher who already redeemed their
//      code on a school computer reach the app from home
//      without a fresh code.
//
// The gate intentionally lives below <AuthProvider> so the signin
// path can use the same auth context the rest of the app does.

import { useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ApiError, api } from "../auth/client";
import { licenseStore } from "../auth/license";

import { Logo } from "./Logo";

interface LicenseGateProps {
  /** Called once a code has been validated and persisted to
   *  localStorage. The parent re-renders into the full app.
   *  Signin doesn't need to call this — AuthProvider's user
   *  state change drives the parent re-render directly. */
  onUnlock: () => void;
}

type Mode = "activate" | "signin";

export function LicenseGate({ onUnlock }: LicenseGateProps) {
  const [mode, setMode] = useState<Mode>("activate");
  const { signin } = useAuth();

  // Activate-tab state.
  const [code, setCode] = useState("");
  const [activatePending, setActivatePending] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);

  // Signin-tab state.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signinPending, setSigninPending] = useState(false);
  const [signinError, setSigninError] = useState<string | null>(null);

  const submitActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length === 0) return;
    setActivatePending(true);
    setActivateError(null);
    try {
      const { code: confirmed } = await api.checkLicense(trimmed);
      licenseStore.set(confirmed);
      onUnlock();
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_or_used") {
        setActivateError(
          "That code isn't valid, or has already been used to create an account. If your account is already linked to it, switch to Sign in instead.",
        );
      } else if (err instanceof Error) {
        setActivateError(err.message);
      } else {
        setActivateError("Couldn't reach the activation server. Try again in a moment.");
      }
    } finally {
      setActivatePending(false);
    }
  };

  const submitSignin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.length === 0 || password.length < 8) return;
    setSigninPending(true);
    setSigninError(null);
    try {
      // signin in AuthProvider also rehydrates licenseStore from
      // the user's bound code, so the next reload short-circuits
      // the gate without re-validating against the server.
      await signin(email, password);
      // The parent (`GatedApp`) detects `user !== null` and
      // renders `<App />`; no need to call onUnlock explicitly.
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_credentials") {
        setSigninError("Email or password is incorrect.");
      } else if (err instanceof Error) {
        setSigninError(err.message);
      } else {
        setSigninError("Couldn't reach the server. Try again in a moment.");
      }
    } finally {
      setSigninPending(false);
    }
  };

  return (
    <div className="license-gate">
      <div className="license-card">
        <div className="license-header">
          <Logo size={64} />
          <div>
            <h1>CoScience.Co</h1>
            <p className="license-tag">Data-driven chemistry / physics sandbox</p>
          </div>
        </div>

        <div className="license-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "activate"}
            className={"license-tab" + (mode === "activate" ? " active" : "")}
            onClick={() => setMode("activate")}
          >
            Activate
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "signin"}
            className={"license-tab" + (mode === "signin" ? " active" : "")}
            onClick={() => setMode("signin")}
          >
            I already have an account
          </button>
        </div>

        {mode === "activate" ? (
          <form className="license-form" onSubmit={submitActivate}>
            <label className="license-field">
              <span>Activation code</span>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                spellCheck={false}
                autoCapitalize="characters"
                autoCorrect="off"
                autoFocus
                disabled={activatePending}
              />
            </label>
            {activateError && <div className="license-error">{activateError}</div>}
            <button
              type="submit"
              className="license-submit"
              disabled={activatePending || code.trim().length === 0}
            >
              {activatePending ? "Checking…" : "Unlock"}
            </button>
          </form>
        ) : (
          <form className="license-form" onSubmit={submitSignin}>
            <label className="license-field">
              <span>Email</span>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={signinPending}
                autoFocus
                required
              />
            </label>
            <label className="license-field">
              <span>Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={signinPending}
                minLength={8}
                required
              />
            </label>
            {signinError && <div className="license-error">{signinError}</div>}
            <button
              type="submit"
              className="license-submit"
              disabled={signinPending || email.length === 0 || password.length < 8}
            >
              {signinPending ? "Signing in…" : "Sign in"}
            </button>
            <p className="license-hint">
              Signing in from a new browser? Use the email and password
              you registered with — your account is permanently linked
              to your activation code.
            </p>
          </form>
        )}

        <div className="license-pwyw">
          <h2>Currently in early access</h2>
          <p>
            <strong>CoScience.Co</strong> is in early stage and operating under{" "}
            <strong>PWYW (Pay What You Want)</strong> so any teacher or
            school can afford it. To arrange an activation code, please
            book a short call:
          </p>
          <a
            className="license-cta"
            href="https://calendly.com/predictexpert"
            target="_blank"
            rel="noopener noreferrer"
          >
            Book on Calendly →
          </a>
        </div>
      </div>
    </div>
  );
}
